import type { PortalRateCardCountry, PortalRateCardProduct } from '@/types/api'

/**
 * The categories present in one country's catalogue, in reading order.
 *
 * Derived rather than fetched: a category is a free-text field on a product, so the list of
 * them is whatever the products say it is. A fixed list would offer a filter that matches
 * nothing the moment somebody renames one.
 */
export function categoriesOf(country: PortalRateCardCountry | undefined): string[] {
  if (!country) return []

  return [...new Set(country.products.map((p) => p.category).filter((c): c is string => !!c))].sort(
    (a, b) => a.localeCompare(b),
  )
}

/**
 * Search and category, in the browser.
 *
 * The catalogue is four products today and will be tens, not thousands - so there is no
 * server-side paging to build and no empty second page to design.
 *
 * The search reaches the module bullets and the ladder's own line names as well as the
 * product name, because a partner looking for "shelf compliance" is looking for the product
 * that does it and does not yet know it is called VisionAI. A search that only matched names
 * would answer "nothing" to the question the page exists for.
 */
export function filterProducts(
  country: PortalRateCardCountry | undefined,
  term: string,
  category: string,
): PortalRateCardProduct[] {
  if (!country) return []

  const needle = term.trim().toLowerCase()

  return country.products.filter((product) => {
    if (category && product.category !== category) return false
    if (needle === '') return true

    return [
      product.name,
      product.tradeName,
      product.sku,
      product.category,
      product.summaryLine,
      ...product.modules,
      ...product.lines.map((line) => line.name),
    ].some((field) => field?.toLowerCase().includes(needle))
  })
}
